/**
 * Сервис для кеширования изображений (thumbnail) в IndexedDB
 * Адаптирован из OpenAIImage для MarketingTool
 */

const DB_NAME = 'CreativesThumbnailCacheDB';
const DB_VERSION = 1;
const STORE_NAME = 'thumbnails';
const CACHE_EXPIRY_DAYS = 30;

class ImageCacheService {
  constructor() {
    this.dbPromise = this.initDB();
  }

  /**
   * Инициализация IndexedDB
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[ImageCache] Ошибка открытия БД:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        console.log('[ImageCache] БД инициализирована');
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'url' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          console.log('[ImageCache] Object store создан');
        }
      };
    });
  }

  /**
   * Проверка валидности кеша
   */
  isValid(timestamp) {
    const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    return Date.now() - timestamp < expiryTime;
  }

  /**
   * Получить изображение из кеша
   */
  async get(url) {
    try {
      const db = await this.dbPromise;
      
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(url);

        request.onsuccess = () => {
          const data = request.result;
          
          if (data && this.isValid(data.timestamp)) {
            console.log(`[ImageCache] ✅ Изображение найдено в кеше: ${url}`);
            resolve(data.blob);
          } else {
            if (data) {
              // Удаляем устаревшее
              this.delete(url);
            }
            resolve(null);
          }
        };

        request.onerror = () => {
          console.error('[ImageCache] Ошибка чтения:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('[ImageCache] Ошибка получения из кеша:', error);
      return null;
    }
  }

  /**
   * Сохранить изображение в кеш
   */
  async set(url, blob) {
    try {
      const db = await this.dbPromise;
      const timestamp = Date.now();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put({ url, blob, timestamp });

        request.onsuccess = () => {
          console.log(`[ImageCache] 💾 Изображение сохранено: ${url} (${(blob.size / 1024).toFixed(2)} KB)`);
          resolve(true);
        };

        request.onerror = () => {
          console.error('[ImageCache] Ошибка сохранения:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('[ImageCache] Ошибка сохранения в кеш:', error);
      return false;
    }
  }

  /**
   * Удалить изображение из кеша
   */
  async delete(url) {
    try {
      const db = await this.dbPromise;

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(url);

        request.onsuccess = () => {
          console.log(`[ImageCache] 🗑️ Изображение удалено: ${url}`);
          resolve(true);
        };

        request.onerror = () => {
          console.error('[ImageCache] Ошибка удаления:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('[ImageCache] Ошибка удаления из кеша:', error);
      return false;
    }
  }

  /**
   * Очистить весь кеш
   */
  async clear() {
    try {
      const db = await this.dbPromise;

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();

        request.onsuccess = () => {
          console.log('[ImageCache] 🗑️ Кеш полностью очищен');
          resolve(true);
        };

        request.onerror = () => {
          console.error('[ImageCache] Ошибка очистки:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('[ImageCache] Ошибка очистки кеша:', error);
      return false;
    }
  }

  /**
   * Очистить устаревшие записи
   */
  async cleanExpired() {
    try {
      const db = await this.dbPromise;

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.openCursor();
        let deletedCount = 0;

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          
          if (cursor) {
            const data = cursor.value;
            
            if (!this.isValid(data.timestamp)) {
              cursor.delete();
              deletedCount++;
            }
            
            cursor.continue();
          } else {
            if (deletedCount > 0) {
              console.log(`[ImageCache] 🧹 Удалено ${deletedCount} устаревших записей`);
            }
            resolve(deletedCount);
          }
        };

        request.onerror = () => {
          console.error('[ImageCache] Ошибка очистки устаревших:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('[ImageCache] Ошибка очистки устаревших записей:', error);
      return 0;
    }
  }

  /**
   * Получить статистику кеша
   */
  async getStats() {
    try {
      const db = await this.dbPromise;

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.openCursor();
        
        let count = 0;
        let totalSize = 0;

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          
          if (cursor) {
            count++;
            totalSize += cursor.value.blob.size;
            cursor.continue();
          } else {
            resolve({
              count,
              totalSize,
              totalSizeMB: (totalSize / 1024 / 1024).toFixed(2)
            });
          }
        };

        request.onerror = () => {
          console.error('[ImageCache] Ошибка получения статистики:', request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('[ImageCache] Ошибка получения статистики:', error);
      return { count: 0, totalSize: 0, totalSizeMB: '0.00' };
    }
  }
}

// Создаем единственный экземпляр сервиса
export const imageCacheService = new ImageCacheService();

// Очищаем устаревшие записи при загрузке
imageCacheService.cleanExpired();

// Экспортируем для отладки
if (typeof window !== 'undefined') {
  window.imageCacheService = imageCacheService;
}
